import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./ConcatenateWithTooltip.module.scss";

const TOOLTIP_MAX_WIDTH = 250;
const TOOLTIP_OFFSET = 6;

const ConcatenateWithTooltip = ({
  children,
  value,
  noOfCharacters,
  useChild = false,
}) => {
  const text = value === null || value === undefined ? "" : `${value}`;
  const limit = Number(noOfCharacters);
  const isTruncated = text.length > limit;
  const triggerRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState(null);

  const getTooltipStyle = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - TOOLTIP_MAX_WIDTH - 8),
    );
    // open above the trigger when it sits in the lower part of the viewport
    const openUpwards = rect.bottom > window.innerHeight * 0.7;
    return openUpwards
      ? { left, bottom: window.innerHeight - rect.top + TOOLTIP_OFFSET }
      : { left, top: rect.bottom + TOOLTIP_OFFSET };
  };

  const showTooltip = () => {
    if (!isTruncated || !triggerRef.current) return;
    setTooltipStyle(getTooltipStyle());
  };

  const hideTooltip = () => setTooltipStyle(null);

  // keep the fixed tooltip attached to its trigger when any container scrolls
  useEffect(() => {
    if (!tooltipStyle) return;
    const reposition = () => {
      if (!triggerRef.current?.matches(":hover")) return hideTooltip();
      setTooltipStyle(getTooltipStyle());
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [!!tooltipStyle]);

  return (
    <span
      ref={triggerRef}
      className={`${styles.concatenateWithTooltip} ${
        isTruncated ? styles.isTruncated : ""
      }`}
      onMouseEnter={showTooltip}
      // mouseenter is missed when the cell re-mounts under a resting pointer
      onMouseMove={() => !tooltipStyle && showTooltip()}
      onMouseLeave={hideTooltip}
    >
      {useChild ? (
        children
      ) : (
        <span>{isTruncated ? text.substring(0, limit) : text}</span>
      )}
      {isTruncated ? (
        <span className={styles.toolTipIcon}>
          <span>...</span>
        </span>
      ) : null}

      {tooltipStyle &&
        createPortal(
          <span
            className={styles.toolTipContent}
            style={{ ...tooltipStyle, maxWidth: TOOLTIP_MAX_WIDTH }}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
};
export default ConcatenateWithTooltip;
